<template>
  <div
    :data-event-id="event.id"
    :data-schema-id="schemaId"
    :data-field-index="depth > 1 ? path[path.length - 1] : undefined"
    :data-depth="depth"
  >
    <template v-for="(field, fieldIndex) in fields" :key="field.name">
      <div :class="depth <= 2 ? 'sortableRow' : ''">
        <div class="field-name d-flex align-center" :style="{ paddingLeft: indent }">
          <v-btn
            :class="depth <= 2 ? 'border-none cursor-handle sortHandle' : 'border-0'"
            size="small"
            variant="plain"
            tabindex="-1"
            icon
            :disabled="disabled || depth > 2"
          >
            <v-icon
              :icon="
                field.type === 'filter' ? 'mdi-filter-variant'
                : field.computed ? 'mdi-function-variant'
                  : 'mds:drag-indicator'
              "
            />
            <v-icon
              v-if="field.type === 'filter' && field.computed"
              class="handle-computed-badge"
              icon="mdi-function-variant"
              size="10"
            />
          </v-btn>
          <quick-rename-input
            :id="fieldId(fieldIndex)"
            :value="field.name"
            @onChange="(e) => onBlur(e, fieldIndex)"
            :density="`compact`"
            :max-length="30"
            style="flex-grow: 1;"
            :disabled="disabled"
          />
          <v-chip
            v-if="field.relatedEntityId && getRelatedEntity(field)"
            density="compact"
            size="small"
            :closable="!disabled && !isMaxDepth"
            class="related-entity-chip px-2 ml-1"
          >
            {{ field.cardinality === 'one-to-many'
              ? `[ ${getRelatedEntity(field)?.name} ]`
              : `${getRelatedEntity(field)?.name}` }}
            <template v-if="!disabled && !isMaxDepth" #close>
              <v-icon
                icon="mdi-close-circle"
                color="#888"
                :size="16"
                class="remove-entity-icon"
                @click.stop="selectRelatedEntityAtPath(event, cardId, currentPath(fieldIndex), null)"
              />
            </template>
          </v-chip>
          <template v-if="depth === 1 && checkRootFieldIssue">
            <v-menu
              v-if="checkRootFieldIssue(field) && rootEntityId && !disabled"
              location="right"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ props: menuProps }">
                <v-tooltip location="right" max-width="300px">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <span v-bind="{ ...menuProps, ...tooltipProps }" style="cursor: pointer;">⚠️</span>
                  </template>
                  <span>This field has no corresponding entity field. <br />Click ⚠️ to fix.</span>
                </v-tooltip>
              </template>
              <v-card min-width="280" class="pa-3">
                <div class="text-body-2 mb-2">
                  This field has no corresponding entity field.
                </div>
                <v-btn
                  color="primary"
                  size="small"
                  variant="tonal"
                  block
                  @click="addFieldToEntityByName(rootEntityId, field.name)"
                >
                  <v-icon start size="small">mdi-plus</v-icon>
                  Add "{{ field.name }}" to {{ getRelatedEntity({ relatedEntityId: rootEntityId })?.name }}
                </v-btn>
              </v-card>
            </v-menu>
            <v-tooltip v-else-if="checkRootFieldIssue(field)" location="right" max-width="300px">
              <template v-slot:activator="{ props: tipProps }">
                <span v-bind="tipProps" style="cursor: default;">⚠️</span>
              </template>
              <span>This field on the read model has no corresponding entity field.</span>
            </v-tooltip>
            <template v-else-if="checkEntityMismatch">
              <v-menu
                v-if="checkEntityMismatch(field) && !disabled"
                location="right"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ props: menuProps }">
                  <v-tooltip location="right" max-width="300px">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <v-icon
                        v-bind="{ ...menuProps, ...tooltipProps }"
                        size="14px"
                        icon="mdi-alert"
                        color="#ED1C0C"
                        style="cursor: pointer;"
                      />
                    </template>
                    <span v-if="!field.relatedEntityId">
                      This field should reference entity '{{ getEntityFieldRelatedEntity(field)?.name }}', but no entity is selected.
                      <br />Click <v-icon size="12px" icon="mdi-alert" color="#ED1C0C" /> to fix.
                    </span>
                    <span v-else>
                      On this read model, the field '{{ field?.name }}' is set to be an entity of type '{{ getRelatedEntity(field)?.name }}'.
                      This does not match with how the field '{{ field?.name }}' is defined under the linked entity.
                      <br />Click <v-icon size="12px" icon="mdi-alert" color="#ED1C0C" /> to fix.
                    </span>
                  </v-tooltip>
                </template>
                <v-card min-width="280" max-width="320" class="pa-3">
                  <div class="text-body-2 mb-2">
                    <span v-if="!field.relatedEntityId">
                      This field should reference entity '{{ getEntityFieldRelatedEntity(field)?.name }}', but no entity is selected.
                    </span>
                    <span v-else-if="getEntityFieldRelatedEntity(field)">
                      This field references '{{ getRelatedEntity(field)?.name }}' but the entity field
                      references '{{ getEntityFieldRelatedEntity(field)?.name }}'.
                    </span>
                    <span v-else>
                      This field references '{{ getRelatedEntity(field)?.name }}' but the linked entity's field has no reference.
                    </span>
                  </div>
                  <v-btn
                    color="primary"
                    size="small"
                    variant="tonal"
                    block
                    @click="fixEntityMismatch(field, fieldIndex)"
                  >
                    <template v-if="getEntityFieldRelatedEntity(field)">
                      <v-icon start size="small">mdi-link</v-icon>
                      Set reference to "{{ getEntityFieldRelatedEntity(field)?.name }}"
                    </template>
                    <template v-else>
                      <v-icon start size="small">mdi-link-off</v-icon>
                      Remove entity reference
                    </template>
                  </v-btn>
                </v-card>
              </v-menu>
              <v-tooltip v-else-if="checkEntityMismatch(field)" location="right" max-width="300px">
                <template v-slot:activator="{ props: tipProps }">
                  <v-icon
                    v-bind="tipProps"
                    size="14px"
                    icon="mdi-alert"
                    color="#ED1C0C"
                    style="cursor: default;"
                  />
                </template>
                <span v-if="!field.relatedEntityId">
                  This field should reference entity '{{ getEntityFieldRelatedEntity(field)?.name }}', but no entity is selected.
                </span>
                <span v-else>
                  On this read model, the field '{{ field?.name }}' is set to be an entity of type '{{ getRelatedEntity(field)?.name }}'.
                  This does not match with how the field '{{ field?.name }}' is defined under the linked entity.
                </span>
              </v-tooltip>
            </template>
          </template>
          <template v-if="depth > 1 && parentRelatedEntityId">
            <v-tooltip v-if="hasFieldIssue(parentRelatedEntityId, field)" location="right">
              <template v-slot:activator="{ props: tipProps }">
                <span v-bind="tipProps" style="cursor: default;">⚠️</span>
              </template>
              <span>This field on the read model has no corresponding <br /> entity field.</span>
            </v-tooltip>
          </template>
          <query-field-actions
            v-if="!disabled"
            :field="field"
            :hide-entity="isMaxDepth"
            @toggle-filter="toggleFieldFilterAtPath(schemaId, currentPath(fieldIndex))"
            @toggle-computed="toggleFieldComputedAtPath(schemaId, currentPath(fieldIndex))"
            @select-entity="(entity) => selectRelatedEntityAtPath(event, cardId, currentPath(fieldIndex), entity)"
            @delete="removeFieldAtPath(event, cardId, currentPath(fieldIndex))"
            @update:cardinality="(cardinality) => selectCardinalityAtPath(event.id, cardId, currentPath(fieldIndex), cardinality)"
          />
        </div>
        <div
          v-if="field.relatedEntityId && !isMaxDepth"
          :data-event-id="event.id"
          :data-schema-id="schemaId"
          :data-field-index="depth === 1 ? fieldIndex : undefined"
        >
          <domain-model-read-model-fields
            :fields="field.fields || []"
            :depth="depth + 1"
            :maxDepth="maxDepth"
            :path="currentPath(fieldIndex)"
            :event="event"
            :cardId="cardId"
            :schemaId="schemaId"
            :disabled="disabled"
            :parentRelatedEntityId="field.relatedEntityId"
            :rootEntityId="rootEntityId"
          />
          <div
            v-if="!disabled"
            class="d-flex align-center btn-add-sub-field"
            :style="{ paddingLeft: childIndent }"
          >
            <v-tooltip location="bottom" open-delay="100">
              <template v-slot:activator="{ on, props: btnProps }">
                <v-btn
                  v-bind="btnProps"
                  v-on="{ ...on }"
                  size="small"
                  variant="plain"
                  :class="depth === 1 ? 'defers-schema-blur' : ''"
                  @mousedown="depth === 1 ? onAddFieldMousedown() : null"
                  @click="onAddField(fieldIndex)"
                  tabindex="-1"
                  icon="mdi-plus"
                />
              </template>
              <span>Add field</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import QuickRenameInput from '@/components/QuickRenameInput'
import QueryFieldActions from '@/components/QueryFieldActions.vue'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelActions from '@/composables/useReadModelActions'
import useEntityActions from '@/composables/useEntityActions'

const props = defineProps({
  fields: { type: Array, default: () => [] },
  depth: { type: Number, default: 1 },
  maxDepth: { type: Number, default: 3 },
  path: { type: Array, default: () => [] },
  event: { type: Object, required: true },
  cardId: { type: String, required: true },
  schemaId: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  parentRelatedEntityId: { type: String, default: null },
  rootEntityId: { type: String, default: null },
  checkRootFieldIssue: { type: Function, default: null },
  checkEntityMismatch: { type: Function, default: null },
  getEntityFieldRelatedEntity: { type: Function, default: null },
  fixEntityMismatch: { type: Function, default: null }
})

const {
  getRelatedEntity,
  hasFieldIssue,
  addFieldAtPath,
  blurFieldAtPath,
  removeFieldAtPath,
  selectRelatedEntityAtPath,
  selectCardinalityAtPath
} = useDomainModel()

const { toggleFieldFilterAtPath, toggleFieldComputedAtPath } = useReadModelActions()

const { addFieldToEntityByName } = useEntityActions()

const isMaxDepth = computed(() => props.depth >= props.maxDepth)
const indent = computed(() => (props.depth - 1) * 16 + 'px')
const childIndent = computed(() => props.depth * 16 + 'px')

const currentPath = (fieldIndex) => [...props.path, fieldIndex]

const fieldId = (fieldIndex) => {
  const pathStr = props.path.length > 0 ? props.path.join('-') + '-' : ''
  return `read-model-${props.event.id}-field-${pathStr}${fieldIndex}`
}

const onBlur = (e, fieldIndex) => {
  blurFieldAtPath(e, props.event, props.schemaId, currentPath(fieldIndex))
}

let skipNextAdd = false
const onAddFieldMousedown = () => {
  skipNextAdd = !!document.activeElement?.id?.startsWith('combobox-input-')
}

const onAddField = (fieldIndex) => {
  if (skipNextAdd) {
    skipNextAdd = false
    return
  }
  addFieldAtPath(props.event, props.cardId, currentPath(fieldIndex))
}
</script>

<style scoped>
.card-menu .v-list-item:hover {
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

.sortableRow:hover > div > .btn-add-sub-field {
  opacity: 1;
  height: 16px;

  .v-btn {
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

.btn-add-sub-field {
  opacity: 0;
  height: 0;
  transition: all 0.2s;
}

.handle-computed-badge {
  position: absolute;
  bottom: -1px;
  right: -1px;
  background: white;
  border-radius: 50%;
}
</style>
